<script lang="ts" setup>
  import { useToast } from '@travelata-libs/ui-kit';
  import { LeadBanner, type LeadBannerPayload } from '@travelata-libs/ui-shared';
  import { useIntersectionObserver } from '@vueuse/core';
  import { ref, computed } from 'vue';
  import {
    LeadBannerGoal,
    MAIN_PAGE_LEAD_BANNER_CONTEXT,
    type LeadBannerTrackingContext,
    useLeadBannerTracking,
  } from '@/entities/analytics';
  import { createLead } from '@/shared/api/createLead';
  import { useTravelataAppConfig } from '@/shared/composables';
  import { LEAD_BANNER_TEXTS } from './config/texts';
  import { setLeadCookie, checkLeadSession } from './lib';

  interface LeadBannerProps {
    title: string;
    subtitle: string;
    mobile?: boolean;
    modalTitle?: string;
    tracking?: LeadBannerTrackingContext;
    buttonText?: string;
  }

  const props = withDefaults(defineProps<LeadBannerProps>(), {
    modalTitle: undefined,
    mobile: false,
    tracking: () => ({ ...MAIN_PAGE_LEAD_BANNER_CONTEXT }),
    buttonText: LEAD_BANNER_TEXTS.submitButton,
  });

  const formRef = ref<InstanceType<typeof LeadBanner> | null>(null);
  const { showToast } = useToast();
  const appConfig = useTravelataAppConfig();
  const { track } = useLeadBannerTracking();

  const isSubmitted = ref<boolean>(checkLeadSession());
  const loading = ref<boolean>(false);
  const model = ref<LeadBannerPayload>({ phone: '', name: '' });

  const targetEl = computed(() => (formRef.value?.$el ?? undefined) as HTMLElement | undefined);

  const trackGoal = (goal: LeadBannerGoal): void => {
    track(goal, props.tracking);
  };

  const onSubmit = async (data: LeadBannerPayload) => {
    isSubmitted.value = false;
    loading.value = true;
    try {
      await createLead(data);
      setLeadCookie();
      isSubmitted.value = true;
      formRef.value?.closeModal();
      trackGoal(LeadBannerGoal.FormSubmit);
    } catch {
      showToast({
        type: 'error',
        title: 'Что-то пошло не так',
        description: 'Заявка не отправлена. Попробуйте ещё раз.',
      });
    } finally {
      loading.value = false;
    }
  };

  const onSheetOpen = () => {
    trackGoal(LeadBannerGoal.SheetOpen);
  };

  const onFocus = () => {
    trackGoal(LeadBannerGoal.FormStart);
  };

  useIntersectionObserver(
    targetEl,
    ([entry]) => {
      if (entry?.isIntersecting) trackGoal(LeadBannerGoal.Impression);
    },
    { threshold: 0 },
  );
</script>

<template>
  <LeadBanner
    ref="formRef"
    v-model="model"
    appearance="compact"
    :title="title"
    :subtitle="subtitle"
    :user-agreement-url="appConfig.legalDocuments.user_agreement"
    :privacy-policy-url="appConfig.legalDocuments.privacy"
    :data-processing-url="appConfig.legalDocuments.personal_data_agreement"
    :is-submitted="isSubmitted"
    :loading="loading"
    :mobile="mobile"
    :modal-title="modalTitle"
    :button-text="buttonText"
    class="lead-banner"
    :class="{ 'lead-banner_mobile': mobile }"
    @sheetOpen="onSheetOpen"
    @focus="onFocus"
    @submit="onSubmit"
  ></LeadBanner>
</template>

<style lang="scss" scoped>
  .lead-banner {
    backdrop-filter: blur(14.6px);
    background: rgb(255 255 255 / 0.15);
    border-color: var(--color-text-inverse);
    border-radius: var(--rounding-xl);
    box-shadow: var(--shadow-xxs);
    box-sizing: border-box;
    color: var(--color-text-inverse);

    &_mobile {
      backdrop-filter: none;
      background: linear-gradient(
        233deg,
        var(--color-cyan-40) 0%,
        var(--color-brand-secondary) 73%,
        var(--color-sky-50) 100%
      );
      border: none;
      box-shadow: 1px 1px 8px 0 rgb(0 0 0 / 0.15);
      padding: var(--spacing-m);
    }

    ::v-deep .tl-button {
      background: transparent;
      border: 1px solid var(--color-text-inverse);
    }
  }
</style>
